import ApiService from "../../../services/ApiService";

const BASE = "/v1/credit";

function buildSort(sort) {
    if (!Array.isArray(sort) || sort.length === 0) return "createdAt:desc";
    return sort.map((s) => `${s.field}:${s.sort}`).join(",");
}

function totalElements(result) {
    if (!result || typeof result !== "object") return 0;
    return result.totalElements ?? result.page?.totalElements ?? 0;
}

function isProductRow(obj) {
    return Boolean(obj && typeof obj === "object" && obj.productId != null);
}

function isProductGroup(obj) {
    return Boolean(obj && typeof obj === "object" && Array.isArray(obj.products));
}

/** Friendly pack name from store product id (Pocket / Pro / Master Pass / …). */
export function creditProductDisplayLabel(product) {
    const explicit =
        product?.label ||
        product?.displayName ||
        product?.name ||
        product?.title ||
        "";
    if (typeof explicit === "string" && explicit.trim()) return explicit.trim();

    const id = String(product?.productId || "").toLowerCase();
    if (!id) return "Product";
    if (id.includes("masterpass") || id.includes("master_pass") || id.includes("master-pass")) {
        return "Master Pass";
    }
    if (id.includes("pocket")) return "Pocket Pack";
    if (id.includes(".pro") || id.endsWith(".pro") || id.includes("_pro") || id.includes("-pro")) {
        return "Pro Pack";
    }
    if (id.includes("admob") || id.includes("watch_ad") || id.includes("watch-ad")) {
        return "Watch ad";
    }
    if (id.includes("hint")) return "Hint Pack";
    const tail = id.split(".").pop() || id;
    return tail.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Normalizes GET /v1/credit/products payloads (§5.2 / §6.5). */
function extractProductGroups(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) {
        if (raw.length === 0) return [];
        if (isProductGroup(raw[0])) return raw;
        if (isProductRow(raw[0])) return [{ group: "", products: raw }];
        return [];
    }
    if (typeof raw !== "object") return [];

    if (raw.code === "FAILURE" || raw.code === "NO_DATA") return [];

    if (Array.isArray(raw.groups)) return raw.groups;
    if (Array.isArray(raw.content)) return raw.content;
    if (Array.isArray(raw.data)) return raw.data;

    const apps = raw.apps;
    if (apps && typeof apps === "object" && !Array.isArray(apps)) {
        return Object.entries(apps).map(([group, val]) => ({
            group,
            products: Array.isArray(val)
                ? val
                : Array.isArray(val?.products)
                  ? val.products
                  : [],
        }));
    }

    if (isProductGroup(raw)) return [raw];
    return [];
}

const CreditService = {
    listApps: async (paginationModel, sort, keyword = "") => {
        const params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: buildSort(sort),
            keyword: keyword || "",
        };
        return ApiService.getData(`${BASE}/apps`, params);
    },

    listAllApps: async () => {
        const result = await ApiService.getData(`${BASE}/apps`, {
            page: 0,
            pageSize: 500,
            sort: "code:asc",
            keyword: "",
        });
        return result?.content ?? [];
    },

    createApp: async ({ code, name }) => ApiService.postData(`${BASE}/apps`, { code, name }),

    updateApp: async (id, { code, name }) => ApiService.putData(`${BASE}/apps/${id}`, { code, name }),

    deleteApp: async (id) => ApiService.deleteData(`${BASE}/apps/${id}`),

    listWallets: async (paginationModel, sort, { userId = "", appCode = "" } = {}) => {
        const params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: buildSort(sort),
        };
        if (userId) params.userId = userId;
        if (appCode) params.appCode = appCode;
        return ApiService.getData(`${BASE}/wallets`, params);
    },

    /** Guest wallets (mo_guest_credits): unsigned devices that watched an ad and received credits. */
    listGuestWallets: async (paginationModel, sort, { deviceId = "", appCode = "", excludeDeviceIds = [] } = {}) => {
        const params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: buildSort(sort),
        };
        if (deviceId) params.deviceId = deviceId;
        if (appCode) params.appCode = appCode;
        if (Array.isArray(excludeDeviceIds) && excludeDeviceIds.length) {
            params.excludeDeviceIds = excludeDeviceIds.join(",");
        }
        return ApiService.getData(`${BASE}/guest-wallets`, params);
    },

    adjustWallet: async ({ userId, appCode, delta, bonus = false }) =>
        ApiService.putData(`${BASE}/wallets/adjust`, {
            userId,
            appCode,
            delta: Number(delta),
            bonus: Boolean(bonus),
        }),

    resetUserAppWallet: async ({ userId, appCode }) =>
        ApiService.postData(`${BASE}/wallets/reset`, { userId, appCode }),

    consumeCredits: async ({ userId, appCode, creditsConsumed, featureUsed = "admin_force_consume" }) =>
        ApiService.postData(`${BASE}/consume`, {
            userId,
            appCode,
            creditsConsumed: Number(creditsConsumed),
            featureUsed,
        }),

    listIap: async (
        paginationModel,
        sort,
        { userId = "", appCode = "", productId = "", excludeStaff = false, excludeAppleStaff = false } = {},
    ) => {
        const params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: buildSort(sort),
        };
        if (userId) params.userId = userId;
        if (appCode) params.appCode = appCode;
        if (productId) params.productId = productId;
        if (excludeStaff) params.excludeStaff = true;
        if (excludeAppleStaff) params.excludeApplePrivateRelay = true;
        return ApiService.getData(`${BASE}/iap`, params);
    },

    listAdRewards: async (paginationModel, sort, { userId = "", appCode = "" } = {}) => {
        const params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: buildSort(sort),
        };
        if (userId) params.userId = userId;
        if (appCode) params.appCode = appCode;
        return ApiService.getData(`${BASE}/ad-rewards`, params);
    },

    listUsage: async (paginationModel, sort, { userId = "", appCode = "" } = {}) => {
        const params = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: buildSort(sort),
        };
        if (userId) params.userId = userId;
        if (appCode) params.appCode = appCode;
        return ApiService.getData(`${BASE}/usage`, params);
    },

    listProducts: async () => ApiService.getData(`${BASE}/products`),

    /**
     * @returns {{ groups: { group: string, products: object[] }[], rows: { id: string, group: string, productId: string, creditsAwarded: number }[] }}
     */
    parseProductCatalog(raw) {
        const groups = extractProductGroups(raw);
        const rows = [];
        groups.forEach((entry) => {
            const groupName =
                typeof entry?.group === "string" && entry.group.trim() ? entry.group.trim() : "—";
            const products = Array.isArray(entry?.products) ? entry.products : [];
            products.forEach((product, productIndex) => {
                if (!product || typeof product !== "object") return;
                const productId = product.productId ?? "";
                rows.push({
                    id: `${groupName}-${productId || productIndex}`,
                    group: groupName,
                    productId,
                    label: creditProductDisplayLabel(product),
                    creditsAwarded: Number(product.creditsAwarded) || 0,
                });
            });
        });
        return { groups, rows };
    },

    normalizeProductCatalog(raw) {
        return CreditService.parseProductCatalog(raw).rows;
    },

    totalElements,
};

export default CreditService;
